import React from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Avatar,
  Badge,
  Tooltip,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Popper,
  ClickAwayListener,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';
import { Search, DarkMode, LightMode, Notifications, Close } from '@mui/icons-material';
import { useTheme } from '../context/ThemeContext';
import { HeaderLogo } from '../components/virtualpat-theme/VirtualPATLogo';
import { useNavigate, useLocation } from 'react-router-dom';
import { getApiUrl } from '../utils/apiConfig';

export default function CommandBar({ onOpenPalette, onNotificationsClick }) {
  const { mode, toggleColorMode } = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const isASC = React.useMemo(() => {
    try {
      return location.pathname.startsWith('/asc') || localStorage.getItem('appMode') === 'asc';
    } catch {
      return location.pathname.startsWith('/asc');
    }
  }, [location.pathname]);
  const [query, setQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [results, setResults] = React.useState([]);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    const handleKeyDown = e => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const extractList = React.useCallback(payload => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.patients)) return payload.patients;
    if (Array.isArray(payload?.results)) return payload.results;
    return [];
  }, []);

  React.useEffect(() => {
    let abort = false;
    async function run() {
      if (!query || query.length < 2) {
        setResults([]);
        return;
      }
      const token = localStorage.getItem('_at') || localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      try {
        const extraASC = isASC
          ? [
              fetch(getApiUrl('/api/asc/surgeons') + `?q=${encodeURIComponent(query)}&limit=5`, {
                headers,
              }),
            ]
          : [];
        const [pRes, tRes, rRes, ...extras] = await Promise.all([
          fetch(
            getApiUrl(isASC ? '/api/asc/patients' : '/api/patients') +
              `?q=${encodeURIComponent(query)}&limit=5`,
            { headers }
          ),
          fetch(getApiUrl('/api/order-templates') + `?limit=5`, { headers }),
          fetch(getApiUrl('/api/protocol-config'), { headers }),
          ...extraASC,
        ]);
        const [pJson, tJson, rJson, ...extraJsons] = await Promise.all([
          pRes.ok ? pRes.json() : { data: [] },
          tRes.ok ? tRes.json() : { data: [] },
          rRes.ok ? rRes.json() : { data: [] },
          ...extras.map(r => (r && r.ok ? r.json() : { data: [] })),
        ]);
        if (abort) return;
        const items = [];
        extractList(pJson).forEach(p =>
          items.push({
            label: `Patient: ${p.lastName}, ${p.firstName} (${p.mrn || p.id})`,
            section: 'Patients',
            to: `/patient/${p.id}`,
          })
        );
        extractList(tJson).forEach(t =>
          items.push({
            label: `Order Template: ${t.displayName || t.name}`,
            section: 'Orders',
            to: `/orders?template=${encodeURIComponent(t.name)}`,
          })
        );
        extractList(rJson)
          .filter(r => {
            try {
              return JSON.stringify(r).toLowerCase().includes(query.toLowerCase());
            } catch {
              return false;
            }
          })
          .slice(0, 5)
          .forEach(r =>
            items.push({
              label: `Rule: ${r.name}`,
              section: 'Protocols',
              to: isASC
                ? `/asc/protocol-management?rule=${encodeURIComponent(r.name)}`
                : `/protocols?rule=${encodeURIComponent(r.name)}`,
            })
          );
        if (isASC && extraJsons[0]) {
          const surgeons = extractList(extraJsons[0]) || extraJsons[0].surgeons || [];
          surgeons
            .slice(0, 5)
            .forEach(s =>
              items.push({
                label: `Surgeon: ${s.lastName}, ${s.firstName}`,
                section: 'Surgeons',
                to: '/asc/surgeon-management',
              })
            );
        }
        // Patient Summary section anchors (local, when on a patient route)
        try {
          const onPatientRoute = location.pathname.startsWith('/patient/');
          const localSections = globalThis.__vp_summarySections || [];
          if (onPatientRoute && Array.isArray(localSections) && localSections.length > 0) {
            const q = query.toLowerCase();
            const ALIASES = {
              'ai-engine': ['ai', 'guidance', 'recommendations', 'insights'],
              'ai-merged-recs': ['unified', 'recommendations', 'readiness', 'apply', 'protocol'],
              'lab-results': ['labs', 'lab', 'cbc', 'chem', 'results'],
              'surgical-clearance': ['clearance', 'status', 'ready', 'readiness', 'asa'],
              'past-medical-history': ['history', 'pmh', 'conditions', 'problems', 'dx'],
              'patient-info': ['demographics', 'info', 'vitals', 'bmi', 'age', 'mrn'],
              'patient-header': ['header', 'summary', 'chips', 'risk', 'alerts'],
              'preop-actions': ['actions', 'preop', 'tasks', 'todos'],
              'predictive-insights': ['predictive', 'risk', 'insights'],
            };
            const seen = new Set();
            localSections.forEach(s => {
              const name = (s?.name || '').toLowerCase();
              const id = s?.id;
              if (!id) return;
              const tokens = name.split(/\s+/g).filter(Boolean);
              const aliases = (ALIASES[id] || []).map(a => a.toLowerCase());
              const haystack = [...tokens, ...aliases];
              const matches = haystack.some(t => t.includes(q) || q.includes(t));
              if (matches && !seen.has(id)) {
                seen.add(id);
                items.unshift({
                  label: `Section: ${s.name}`,
                  section: 'Patient Summary',
                  scrollToSection: id,
                });
              }
            });
          }
        } catch {
          /* noop */
        }
        setResults(items);
      } catch {
        setResults([]);
      }
    }
    run();
    return () => {
      abort = true;
    };
  }, [query]);

  return (
    <Box
      sx={{
        height: 48,
        borderBottom: theme => `1px solid ${theme.palette.divider}`,
        display: 'flex',
        alignItems: 'center',
        px: 1,
        gap: 1,
        position: 'sticky',
        top: 0,
        zIndex: 10,
        bgcolor: theme => theme.palette.background.paper,
      }}
    >
      <TextField
        size="small"
        fullWidth
        inputRef={inputRef}
        placeholder={isMobile ? 'Search…' : 'Search patients, orders, protocols… (Ctrl+K)'}
        value={query}
        onChange={e => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onKeyDown={e => {
          if (e.key === 'Escape') {
            setQuery('');
            setOpen(false);
            inputRef.current?.blur();
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" color={query ? 'primary' : 'inherit'} />
            </InputAdornment>
          ),
          endAdornment: query ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => {
                  setQuery('');
                  setResults([]);
                  setOpen(false);
                  inputRef.current?.focus();
                }}
                edge="end"
                sx={{ mr: -0.5 }}
              >
                <Close fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{ flex: 1, minWidth: isMobile ? 0 : 240, maxWidth: 'unset' }}
      />
      <Popper
        open={open && query.length >= 2}
        anchorEl={inputRef.current}
        placement="bottom-start"
        style={{ zIndex: 11 }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper
            elevation={3}
            sx={{
              mt: 1,
              width: isMobile ? 'calc(100vw - 32px)' : 720,
              maxWidth: isMobile ? 'calc(100vw - 32px)' : 'calc(100vw - 120px)',
              maxHeight: 400,
              overflow: 'auto',
            }}
          >
            {results.length > 0 ? (
              <List dense sx={{ py: 0.5 }}>
                {results.map((r, i) => (
                  <ListItemButton
                    key={i}
                    onClick={() => {
                      if (r.scrollToSection) {
                        try {
                          if (
                            globalThis.__virtualizedSectionList &&
                            typeof globalThis.__virtualizedSectionList.scrollToSection ===
                              'function'
                          ) {
                            globalThis.__virtualizedSectionList.scrollToSection(r.scrollToSection);
                          } else {
                            const el = document.getElementById(`section-${r.scrollToSection}`);
                            if (el && typeof el.scrollIntoView === 'function') {
                              el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                          }
                        } catch {
                          /* noop */
                        }
                        setOpen(false);
                        setQuery('');
                        return;
                      }
                      if (r.to) navigate(r.to);
                      setOpen(false);
                      setQuery('');
                    }}
                    sx={{ py: 1, '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <ListItemText
                      primary={r.label}
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {r.section}
                        </Typography>
                      }
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                  </ListItemButton>
                ))}
              </List>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No results found for "{query}"
                </Typography>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  Try searching for patient names, MRNs, or order templates
                </Typography>
              </Box>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
      {/* Right-aligned controls */}
      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {!isMobile && (
          <Tooltip title={mode === 'dark' ? 'Switch to light' : 'Switch to dark'}>
            <IconButton onClick={toggleColorMode} size="small">
              {mode === 'dark' ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Notifications">
          <IconButton size="small" onClick={() => onNotificationsClick?.()}>
            <Badge color="error" variant="dot">
              <Notifications fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>
        <Tooltip title="Profile">
          <IconButton size="small" onClick={() => navigate(isASC ? '/asc/profile' : '/profile')}>
            <Avatar sx={{ width: isMobile ? 22 : 24, height: isMobile ? 22 : 24 }}>U</Avatar>
          </IconButton>
        </Tooltip>
        {/* VirtualPAT logo */}
        {!isMobile && (
          <Box sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>
            <HeaderLogo height={28} onClick={() => navigate('/')} alt="VirtualPAT" />
          </Box>
        )}
      </Box>
    </Box>
  );
}
